import { unreadCount } from '@symbo-ls/workspace-chat/functions/chatUtils.js'
// Bare shared-library import, not deep workspacePrefix.js / appHeader.js
// subpaths — see app.js's identical fix note (tickets/catalog-fixall.md P0).
// Deferred-call wrappers, not a module-scope destructure — see app.js's
// circular-init note.
import _wsShared from '@symbo-ls/workspace-shared'
import { shellStripWorkspacePrefix } from '../functions/shellSharedDelegates.js'
const shellCheckActiveMember = (...args) => _wsShared.functions.checkActiveMember(...args)
const isIntranetRoute = (...args) => _wsShared.functions.isIntranetRoute(...args)

// Local page-title map for all /tickets/* (and other) routes that
// IntranetAppHeader must label correctly. Mirrors the TITLES map in
// editor/packages/shared/components/AppHeader.js — kept here so the
// intranet bundle is self-sufficient regardless of how Parcel resolves
// the shared AppHeader at build time.
const INTRANET_TITLES = {
  '/calendar': 'workspace.pageTitle.calendar',
  '/chat': 'workspace.pageTitle.chat',
  // Canonical /tasks keys + legacy /tickets aliases (tickets→tasks rename
  // 2026-08-06 per Nika) — alias URLs stay reachable, so both key sets label.
  '/tasks': 'workspace.pageTitle.myView',
  '/tasks/my': 'workspace.pageTitle.myView',
  '/tasks/general': 'workspace.pageTitle.general',
  '/tasks/feed': 'workspace.pageTitle.feed',
  '/tasks/canvas': 'workspace.pageTitle.canvas',
  '/tasks/stories': 'workspace.pageTitle.stories',
  '/tasks/epics': 'workspace.pageTitle.epics',
  '/tasks/board': 'workspace.pageTitle.board',
  '/tasks/standups': 'workspace.pageTitle.standups',
  '/tasks/release': 'workspace.pageTitle.release',
  '/tasks/roadmap': 'workspace.pageTitle.roadmap',
  '/tickets': 'workspace.pageTitle.myView',
  '/tickets/my': 'workspace.pageTitle.myView',
  '/tickets/general': 'workspace.pageTitle.general',
  '/tickets/feed': 'workspace.pageTitle.feed',
  '/tickets/canvas': 'workspace.pageTitle.canvas',
  '/tickets/stories': 'workspace.pageTitle.stories',
  '/tickets/epics': 'workspace.pageTitle.epics',
  '/tickets/board': 'workspace.pageTitle.board',
  '/tickets/standups': 'workspace.pageTitle.standups',
  '/tickets/requests': 'workspace.pageTitle.requests',
  '/tickets/requests/inbound': 'workspace.pageTitle.inbound',
  '/tickets/requests/outbound': 'workspace.pageTitle.outbound',
  '/tickets/requests/audit': 'workspace.pageTitle.audit',
  '/tickets/resolutions': 'workspace.pageTitle.resolutions',
  '/tickets/release': 'workspace.pageTitle.release',
  '/tickets/roadmap': 'workspace.pageTitle.roadmap',
  '/meet': 'workspace.pageTitle.meet',
  '/system-status': 'workspace.pageTitle.systemHealth'
}

const INTRANET_PREFIX_TITLES = [
  ['/tickets/requests', 'workspace.pageTitle.requests'],
  ['/tickets/resolutions', 'workspace.pageTitle.resolutions'],
  ['/tasks', 'workspace.pageTitle.tasks'],
  ['/task/', 'workspace.pageTitle.tasks'],
  ['/tickets', 'workspace.pageTitle.tasks'],
  ['/meet/', 'workspace.pageTitle.call'],
  ['/notes/', 'workspace.pageTitle.notes'],
  ['/notes', 'workspace.pageTitle.notes'],
  // Legacy alias — documents→notes rename (2026-08-06 per Nika); '/documents'
  // stays reachable so a stale deep link still titles correctly.
  ['/documents/', 'workspace.pageTitle.notes'],
  ['/documents', 'workspace.pageTitle.notes'],
  ['/workspaces', 'workspace.pageTitle.workspaces'],
  ['/admin', 'workspace.pageTitle.admin']
]

// Every entry of the two tables above is a bundle KEY; this is the one place
// they are resolved, so the header title follows a language switch through the
// element's own reactive effect.
const intranetPageTitle = (el, path) => {
  path = shellStripWorkspacePrefix(path)
  if (!path) return ''
  if (INTRANET_TITLES[path]) return el.call('polyglot', INTRANET_TITLES[path])
  if (path.startsWith('/meet/') && path.endsWith('/transcript'))
    return el.call('polyglot', 'workspace.pageTitle.transcript')
  for (const [prefix, label] of INTRANET_PREFIX_TITLES) {
    if (path.startsWith(prefix)) return el.call('polyglot', label)
  }
  return el.call('polyglot', 'workspace.pageTitle.canvas')
}

// The rail rows carry bundle KEYS; `AppNavLink` lives in the shared package
// and renders `s.label` verbatim, so the labels are resolved where the list
// is PRODUCED (below) rather than at that render site.
const NAV_LINKS = [
  { label: 'workspace.nav.main', href: '/' },
  { label: 'workspace.nav.teamMembers', href: '/team-members' },
  // Contacts moved to the system/crm-app marketplace module (correct
  // Manifest v2 shape — see /add-app) — no longer a local nav pill.
  { label: 'workspace.nav.my', href: '/my' },
  { label: 'workspace.nav.announcements', href: '/announcements' },
  { label: 'workspace.nav.company', href: '/company' }
]

const totalUnread = (root) => {
  if (!root) return 0
  const me = root.currentUserEmail
  const members = root.channelMembers || []
  let total = 0
  for (const mm of members) {
    if (mm.member_email !== me) continue
    const msgs = root.messagesByChannel?.[mm.channel_id] || []
    total += unreadCount(msgs, mm.last_read_at, me)
  }
  return total
}

export const IntranetAppHeader = {
  extends: 'AppHeader',

  onRender: (el, s) => {
    if (!el._notifInit && s.root?.currentUserEmail) {
      el._notifInit = true
      el.call('requestBrowserPermission')
      el.call('subscribeNotifications')
      el.call('fetchNotifications')
    }
  },

  // Global Cmd+K / Ctrl+K handler. Registered once on first render so it
  // persists across SPA route changes and covers every workspace page.
  // On /chat the chat page owns searchOpen; on all other routes we focus
  // the SearchBar input that lives in the header.
  // Cmd/Ctrl+K focuses the header SearchBar. CAPTURE phase, so the header wins
  // the shortcut before a page-level handler can take it.
  //
  // This was an `onCreate` that stamped `el._cmdKHandler` and called
  // `document.addEventListener('keydown', …, true)` behind an `el._cmdKBound`
  // latch, undone by an `onRemove`. The latch is the tell: `onCreate` CAN fire
  // twice on one element, and without it the second pass would have added a
  // second capture listener and orphaned the first. Declared, the framework
  // registers exactly once and disposes it — the latch is unnecessary (FA503).
  onDocumentKeydown: {
    capture: true,
    handler: (ev, el, s) => {
      if (!(ev.metaKey || ev.ctrlKey) || ev.key.toLowerCase() !== 'k') return
      // A shell layer above the page (shared functions/shellOverlayOpen.js —
      // a dialog, a menu, the assistant's main thread) owns the shortcut: this
      // capture handler would focus a search nobody sees and spend the key.
      if (
        typeof el.context?.functions?.shellOverlayOpen === 'function' &&
        el.call('shellOverlayOpen')
      )
        return
      const root = s.root
      if (!root) return
      // Agent mode's frame takes ⌘K for its own palette (WS-AGENT-MODE-1).
      if (root.agentMode === true || root._agentModeLink === true) return
      const path = shellStripWorkspacePrefix(root.currentPath || root.route || '/')
      // /chat owns its own search panel; let the chat page handle it.
      if (path === '/chat') return
      ev.preventDefault()
      // Focus the global SearchBar input in the header.
      const inp =
        el.node &&
        el.node.querySelector(
          '[data-key="SearchBar"] input, [data-key="SearchBar"] [contenteditable]'
        )
      if (inp) {
        inp.focus()
        inp.select?.()
        return
      }
      // Fallback: open the nav search via root state.
      root.update({ _navSearchOpen: true, navSearchQuery: '' })
    }
  },

  // Recompute chat unread only when the underlying data changed — otherwise
  // every unrelated root update would loop through all memberships.
  onUpdate: (el, s) => {
    const root = s.root
    const members = root.channelMembers
    const msgs = root.messagesByChannel
    if (el._lastMembers === members && el._lastMsgs === msgs) return
    el._lastMembers = members
    el._lastMsgs = msgs
    const next = totalUnread(root)
    const cur = root.navUnreadCounts?.chat || 0
    if (next !== cur) {
      root.update({
        navUnreadCounts: { ...(root.navUnreadCounts || {}), chat: next }
      })
    }
  },

  // Override the shared AppHeader PageTitle so the intranet bundle resolves
  // the correct sub-page label from its own TITLES map rather than the
  // shared one (which may not reflect the latest keys when Parcel caches
  // an older build of AppHeader.js). All /tickets/* routes that were
  // previously falling through to the PREFIX_TITLES catch-all are handled
  // by INTRANET_TITLES above.
  PageTitle: {
    text: (el, s) => intranetPageTitle(el, s.root?.currentPath || s.root?.route || '')
  },

  Links: {
    hide: (el, s) => !shellCheckActiveMember(el) || !isIntranetRoute(s),
    role: 'navigation',
    'aria-label': (el) => el.call('polyglot', 'workspace.header.primaryNav'),
    theme: 'dialog',
    round: 'radiusPill',
    flow: 'x',
    gap: 'X',
    padding: 'X',
    align: 'center center',
    margin: '- auto',
    // Overflow/truncation source (P3 clip): a bounded-width ancestor (a
    // catalog cell, a narrow viewport) let this 5-pill row overflow its own
    // box and get cut off by that ancestor's `overflow:hidden` — nothing
    // here previously bounded the row's own width. Reuses the EXACT already-
    // shipped recipe for this same "row of nav pills in a header might not
    // fit" problem: HeaderTabs.Tabs (shared/components/HeaderTabs.js,
    // 2026-08-07 per Nika "instead of ... on each item, just show them and
    // fade away with shadow whats not fitting"). Scrolls instead of clipping;
    // scrollbar chrome suppressed (InstallableRail idiom); the cut-off edge
    // fades via mask instead of a hard crop.
    minWidth: '0',
    maxWidth: '100%',
    flexShrink: '1',
    overflowX: 'auto',
    overflowY: 'hidden',
    overscrollBehaviorX: 'contain',
    scrollbarWidth: 'none',
    '::-webkit-scrollbar': { display: 'none' },
    maskImage:
      'linear-gradient(to right, transparent, black calc(var(--spacing-V)), black calc(100% - var(--spacing-V)), transparent)',
    childExtends: 'AppNavLink',
    childrenAs: 'state',
    children: (el) => NAV_LINKS.map((n) => ({ ...n, label: el.call('polyglot', n.label) }))
  },

  SearchBar: {
    hide: (el, s) => !shellCheckActiveMember(el) || isIntranetRoute(s),
    Form: {
      Input: {
        placeholder: (el) => el.call('polyglot', 'workspace.nav.searchPlaceholder'),
        onInput: (e, el) => el.call('globalSearch', e.target.value),
        // Close on blur — same ~200ms grace + context-menu guard as the
        // canvas-header SearchBar input (shared/components/HeaderSearch.js
        // onBlur, the OTHER NavSearchResultGroup mount point): a
        // right-clicked result's "Pin to navbar" menu (NavSearchResultItem.
        // onContextmenu, opened with `fromNavSearch: true`) blurs this input
        // by design — the house ContextDropdown is a global portal, never a
        // descendant of this pill, so it can't be caught by a `contains`
        // check. Skip the close while it's up; resume the instant it closes
        // (Escape / outside click / the Pin action all null out
        // `contextDropdown` — nothing to separately track here either).
        onBlur: (e, el) => {
          const pill = el.parent?.parent
          setTimeout(() => {
            if (pill?.node && pill.node.contains(document.activeElement)) return
            const dd = el.state?.root?.contextDropdown
            if (dd && dd.open && dd.fromNavSearch) return
            el.call('closeNavSearch')
          }, 200)
        },
        onKeydown: (e, el) => {
          if (e.key === 'Escape') {
            el.call('closeNavSearch')
            el.node.blur()
          }
        }
      }
    },
    Dropdown: {
      // Explicit state gate — matches HeaderSearch.js's own Dropdown exactly
      // (the base Dropdown's `:focus-within` reveal, inherited via SearchBar
      // → DropdownParentFocus, is the wrong gate for a QUERY-driven panel:
      // focus ≠ has results, and it can never survive focus moving to the
      // "Pin to navbar" context menu — a portal outside this pill's subtree —
      // the way this flag does).
      subscribeTo: ['_navSearchOpen'],
      hide: (el, s) => !s.root?._navSearchOpen,
      opacity: '1',
      visibility: 'visible',
      transform: 'translate3d(0,0,0)',
      ResultGroups: {
        flow: 'y',
        if: (el, s) => !!s.root?._navSearchGrouped?.length,
        children: (el, s) => s.root?._navSearchGrouped || [],
        childExtends: 'NavSearchResultGroup',
        childrenAs: 'state'
      }
    }
  },

  // Shared AppHeader now groups the right cluster (AppOrgSwitcher,
  // OnlineMembers, MeWrapper) into the HeaderRight glass island — intranet
  // gates that whole island on membership and customises the avatar menu.
  // Re-pathed from the old `UserActions` slot to `HeaderRight` for the
  // 2026-06-08 glass-island grouping (the AppHeader flatten had left this
  // override targeting the now-removed `UserActions`, so the Admin item +
  // membership gate had silently stopped applying).
  HeaderRight: {
    hide: (el) => !shellCheckActiveMember(el)
    // The avatar menu's Admin (superadmin-gated) + Sign out rows live in the
    // shared AppHeader's dropdown-plugin menu now (2026-06-10 migration) —
    // the old MeWrapper.MeMenu child overrides here would render as stray
    // nodes since the in-page MeMenu no longer exists.
  }
}
